import { useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { logger } from '@/lib/logger';

export const PWAUpdatePrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [updateFn, setUpdateFn] = useState<(() => void) | null>(null);
  const [countdown, setCountdown] = useState(5);

  // Auto-update after 5 seconds
  useEffect(() => {
    if (!showPrompt) return;

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          // Time's up, trigger update
          if (updateFn) {
            updateFn();
          } else {
            window.location.reload();
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [showPrompt, updateFn]);

  useEffect(() => {
    // PWA is only available in production builds
    if (import.meta.env.DEV) {
      return;
    }

    const initPWA = async () => {
      try {
        if (!('serviceWorker' in navigator)) {
          return;
        }

        // Timeout global pour éviter le blocage
        const initTimeout = setTimeout(() => {
          logger.log('⚠️ PWA: Init timeout, continuing without SW');
        }, 5000);

        // Au premier chargement, vérifier si on doit forcer un refresh
        const lastVersion = localStorage.getItem('app-build-version');
        const currentVersion = import.meta.env.VITE_BUILD_TIME || Date.now().toString();
        
        // Skip auto-reload if already reloaded recently (prevent loop)
        const lastReload = localStorage.getItem('app-last-reload');
        const now = Date.now();
        const reloadCooldown = 60000; // 1 minute cooldown
        
        if (lastVersion && lastVersion !== currentVersion) {
          if (!lastReload || (now - parseInt(lastReload)) > reloadCooldown) {
            logger.log('🔄 PWA: Nouvelle version détectée, nettoyage du cache...');
            // Nettoyer tous les caches
            if ('caches' in window) {
              const cacheNames = await caches.keys();
              await Promise.all(cacheNames.map(name => caches.delete(name)));
            }
            localStorage.setItem('app-build-version', currentVersion);
            localStorage.setItem('app-last-reload', now.toString());
            clearTimeout(initTimeout);
            window.location.reload();
            return;
          }
        }
        
        localStorage.setItem('app-build-version', currentVersion);
        clearTimeout(initTimeout);

        // Force check for updates immediately on page load
        const registrations = await navigator.serviceWorker.getRegistrations();
        
        for (const registration of registrations) {
          try {
            await registration.update();
          } catch (err) {
            // Ignore InvalidStateError which occurs when SW is in bad state
            if (!(err instanceof Error && err.name === 'InvalidStateError')) {
              logger.error('SW update error:', err);
            }
          }
          
          if (registration.waiting) {
            logger.log('🔄 PWA: Activating waiting service worker');
            registration.waiting.postMessage({ type: 'SKIP_WAITING' });
          }
        }

        const registration = await navigator.serviceWorker.ready;
        
        // Check for updates every 15 seconds (more aggressive)
        const checkForUpdates = () => {
          registration.update().catch(logger.error);
        };
        
        const intervalId = setInterval(checkForUpdates, 15 * 1000);

        // Listen for new service worker installing
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (!newWorker) return;

          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              logger.log('🔄 PWA: New version installed, activating...');
              newWorker.postMessage({ type: 'SKIP_WAITING' });
              setShowPrompt(true);
              setCountdown(3); // Réduit à 3 secondes
              setUpdateFn(() => () => {
                window.location.reload();
              });
            }
          });
        });

        // Listen for controlling service worker changes
        navigator.serviceWorker.addEventListener('controllerchange', () => {
          logger.log('🔄 PWA: Controller changed, reloading...');
          window.location.reload();
        });

        return () => clearInterval(intervalId);
      } catch (error) {
        logger.log('PWA update check not available:', error);
      }
    };

    initPWA();
  }, []);

  const handleUpdate = () => {
    if (updateFn) {
      updateFn();
    } else {
      // Fallback: just reload
      window.location.reload();
    }
    setShowPrompt(false);
  };

  // Don't render anything in development or if no update is available
  if (import.meta.env.DEV || !showPrompt) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 z-[100] sm:left-auto sm:right-4 sm:max-w-sm animate-fade-in">
      <div className="bg-[#0A2540] text-white rounded-xl shadow-2xl p-4 border border-white/10">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-[#F57C00] flex items-center justify-center flex-shrink-0 animate-spin-slow">
            <RefreshCw className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="font-bold text-sm mb-1">Mise à jour en cours...</h3>
            <p className="text-white/70 text-xs mb-2">
              Une nouvelle version est disponible. L'application va s'actualiser automatiquement.
            </p>
            <div className="flex items-center gap-2">
              <div className="h-1.5 flex-1 bg-white/20 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-[#F57C00] transition-all duration-1000 ease-linear"
                  style={{ width: `${((5 - countdown) / 5) * 100}%` }}
                />
              </div>
              <span className="text-xs text-white/60 font-medium min-w-[20px]">{countdown}s</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
